'use client';

import React, {
    ForwardedRef,
    forwardRef,
    useMemo,
    useRef,
    useState,
} from 'react';
import { QRCode } from 'react-qrcode-logo';

import { cn } from '@/app/_helper/tailwind';
import { useEffectCallOnce } from '@/app/_hooks/useEffectCallOnce';

import { nameAlias } from '../../_helper/formattedValue';
import { useAgent } from '../../_provider/AgentProvider';

const NAME_CARD_FRONT = `${process.env.NEXT_PUBLIC_BASE_URL || 'https://rumah123portal.staging.99iddev.net'}/asset-core/images/agent/name-card-front.png`;

const CardFront = forwardRef(
    (
        {
            className,
            isValidImage,
            profilePicture,
        }: {
            className?: string;
            isValidImage?: boolean;
            profilePicture?: string;
        },
        forwardedRef: ForwardedRef<any>,
    ) => {
        const [fullUrl, setFullUrl] = useState('');
        const qrCodeRef = useRef<QRCode>(null);
        const {
            name,
            address,
            organizationName,
            companyPicture,
            agentType,
            contacts,
        } = useAgent();

        useEffectCallOnce(() => {
            if (window)
                setFullUrl(
                    `${window.location.href.split('?')[0]}?utm_medium=share&utm_source=qr`,
                );
        });

        const emailAgent = useMemo(() => {
            const emailFound = contacts.find(
                (contact) => contact.type === 'EMAIL',
            );

            return emailFound?.value || '';
        }, [contacts]);

        const phoneAgent = useMemo(() => {
            const waFound = contacts.find(
                (contact) => contact.type === 'WHATSAPP',
            );
            const phoneFound = contacts.find(
                (contact) => contact.type === 'PHONE_NUMBER',
            );

            return phoneFound?.value || waFound?.value || '';
        }, [contacts]);

        return (
            <div
                ref={forwardedRef}
                className={cn(
                    'relative overflow-hidden max-w-[900px] w-[900px] aspect-[900/500] bg-[#F3F3F3]',
                    className,
                )}
            >
                <div className='w-[65px] h-full bg-gradient-to-b from-[#8ba4d2] to-[#2951a3] absolute top-0 left-[100px] z-1' />
                {
                    // NAMA
                }
                <div className='absolute top-[118px] left-[62px] z-[2] flex items-center gap-[50px] w-full max-w-[797px]'>
                    {isValidImage && profilePicture ? (
                        <div className='flex-none w-[145px] h-[145px] aspect-square rounded-[10px] border-2 border-solid border-[#f0f0f0] overflow-hidden'>
                            <img
                                className='w-[143px] h-[143px] object-scale-down object-center bg-white'
                                src={profilePicture?.replace('1000x1000-crop', '300x300-crop')}
                                alt={name}
                                title={name}
                            />
                        </div>
                    ) : (
                        <div
                            className={`flex-none w-[145px] h-[145px] aspect-square rounded-[10px] border-2 border-solid border-[#f0f0f0] overflow-hidden text-5xl font-bold text-[#B6BAC2] bg-[#f8f8f8] flex items-center justify-center`}
                        >
                            {nameAlias(name || '')}
                        </div>
                    )}
                    <div className='flex flex-col gap-5'>
                        <div className='flex flex-col gap-2'>
                            <div className='text-[40px] font-bold text-accent'>
                                {name}
                            </div>
                            <div className='text-lg font-normal text-greyText'>
                                {agentType === 'Agen Korporat'
                                    ? organizationName
                                    : agentType}
                            </div>
                        </div>
                        <div className='flex flex-col gap-4'>
                            <div className='flex items-center gap-2'>
                                {phoneAgent && (
                                    <div className='flex items-center gap-2'>
                                        <svg
                                            xmlns='http://www.w3.org/2000/svg'
                                            width='25'
                                            height='25'
                                            viewBox='0 0 25 25'
                                            fill='none'
                                        >
                                            <path
                                                d='M12.0459 2.08008C17.5689 2.08008 22.0459 6.55708 22.0459 12.0801C22.0459 17.6031 17.5689 22.0801 12.0459 22.0801C10.2787 22.0831 8.5425 21.6154 7.01591 20.7251L2.04991 22.0801L3.40191 17.1121C2.51087 15.585 2.04282 13.8481 2.04591 12.0801C2.04591 6.55708 6.52291 2.08008 12.0459 2.08008ZM8.63791 7.38008L8.43791 7.38808C8.3086 7.39699 8.18226 7.43095 8.06591 7.48808C7.95749 7.54959 7.85848 7.62638 7.77191 7.71608C7.65191 7.82908 7.58391 7.92708 7.51091 8.02208C7.14104 8.50298 6.94189 9.09339 6.94491 9.70008C6.94691 10.1901 7.07491 10.6671 7.27491 11.1131C7.68391 12.0151 8.35691 12.9701 9.24491 13.8551C9.45891 14.0681 9.66891 14.2821 9.89491 14.4811C10.9983 15.4525 12.3132 16.153 13.7349 16.5271L14.3029 16.6141C14.4879 16.6241 14.6729 16.6101 14.8589 16.6011C15.1501 16.5857 15.4344 16.5069 15.6919 16.3701C15.8228 16.3024 15.9506 16.229 16.0749 16.1501C16.0749 16.1501 16.1172 16.1214 16.1999 16.0601C16.3349 15.9601 16.4179 15.8891 16.5299 15.7721C16.6139 15.6854 16.6839 15.5847 16.7399 15.4701C16.8179 15.3071 16.8959 14.9961 16.9279 14.7371C16.9519 14.5391 16.9449 14.4311 16.9419 14.3641C16.9379 14.2571 16.8489 14.1461 16.7519 14.0991L16.1699 13.8381C16.1699 13.8381 15.2999 13.4591 14.7679 13.2171C14.7122 13.1928 14.6526 13.1789 14.5919 13.1761C14.5235 13.1689 14.4543 13.1766 14.3891 13.1985C14.3239 13.2204 14.2641 13.2561 14.2139 13.3031C14.2089 13.3011 14.1419 13.3581 13.4189 14.2341C13.3774 14.2898 13.3203 14.332 13.2547 14.3551C13.1892 14.3783 13.1182 14.3814 13.0509 14.3641C12.9857 14.3467 12.9219 14.3247 12.8599 14.2981C12.7359 14.2461 12.6929 14.2261 12.6079 14.1901C12.0338 13.94 11.5024 13.6016 11.0329 13.1871C10.9069 13.0771 10.7899 12.9571 10.6699 12.8411C10.2765 12.4643 9.93366 12.0381 9.64991 11.5731L9.59091 11.4781C9.54918 11.4139 9.51495 11.3451 9.48891 11.2731C9.45091 11.1261 9.54991 11.0081 9.54991 11.0081C9.54991 11.0081 9.79291 10.7421 9.90591 10.5981C10.0159 10.4581 10.1089 10.3221 10.1689 10.2251C10.2869 10.0351 10.3239 9.84008 10.2619 9.68908C9.98191 9.00508 9.69258 8.32474 9.39391 7.64808C9.33491 7.51408 9.15991 7.41808 9.00091 7.39908C8.94691 7.39241 8.89291 7.38708 8.83891 7.38308C8.70464 7.37538 8.57001 7.37671 8.43591 7.38708L8.63791 7.38008Z'
                                                fill='#2951A3'
                                            />
                                        </svg>
                                        <div className='text-base font-medium text-accent'>
                                            {phoneAgent}
                                        </div>
                                    </div>
                                )}
                                {emailAgent && (
                                    <div className='flex items-center gap-2'>
                                        <svg
                                            xmlns='http://www.w3.org/2000/svg'
                                            width='25'
                                            height='25'
                                            viewBox='0 0 25 25'
                                            fill='none'
                                        >
                                            <g clipPath='url(#clip0_289_9305)'>
                                                <path
                                                    d='M21.598 4.08008C21.5072 4.07072 21.4156 4.07072 21.3247 4.08008H2.65802C2.53838 4.08193 2.41954 4.09986 2.30469 4.13342L11.938 13.7267L21.598 4.08008Z'
                                                    fill='#2951A3'
                                                />
                                                <path
                                                    d='M22.5854 5.00674L12.8787 14.6734C12.6289 14.9217 12.2909 15.0611 11.9387 15.0611C11.5865 15.0611 11.2485 14.9217 10.9987 14.6734L1.3787 5.08008C1.34912 5.18877 1.33344 5.30077 1.33203 5.41341V18.7467C1.33203 19.1004 1.47251 19.4395 1.72256 19.6896C1.9726 19.9396 2.31174 20.0801 2.66536 20.0801H21.332C21.6857 20.0801 22.0248 19.9396 22.2748 19.6896C22.5249 19.4395 22.6654 19.1004 22.6654 18.7467V5.41341C22.6601 5.27451 22.6331 5.1373 22.5854 5.00674ZM3.5787 18.7467H2.65203V17.7934L7.4987 12.9867L8.4387 13.9267L3.5787 18.7467ZM21.3187 18.7467H20.3854L15.5254 13.9267L16.4654 12.9867L21.312 17.7934L21.3187 18.7467Z'
                                                    fill='#2951A3'
                                                />
                                            </g>
                                            <defs>
                                                <clipPath id='clip0_289_9305'>
                                                    <rect
                                                        width='24'
                                                        height='24'
                                                        fill='white'
                                                        transform='translate(0.0449219 0.0800781)'
                                                    />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <div className='text-base font-medium text-accent'>
                                            {emailAgent}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {agentType && agentType === 'Agen Korporat' && (
                                <div className='flex items-start gap-2'>
                                    <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='24'
                                        height='25'
                                        viewBox='0 0 24 25'
                                        fill='none'
                                    >
                                        <g clipPath='url(#clip0_289_9311)'>
                                            <path
                                                d='M21 23.5V6.99997H16.5V2.49997H3V23.5H0V25H10.5V20.5H13.5V25H24V23.5H21ZM9 17.5H6V14.5H9V17.5ZM9 13H6V9.99997H9V13ZM9 8.49997H6V5.49997H9V8.49997ZM13.5 17.5H10.5V14.5H13.5V17.5ZM13.5 13H10.5V9.99997H13.5V13ZM13.5 8.49997H10.5V5.49997H13.5V8.49997ZM19.5 17.5H16.5V14.5H19.5V17.5ZM19.5 13H16.5V9.99997H19.5V13Z'
                                                fill='#2951A3'
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id='clip0_289_9311'>
                                                <rect
                                                    width='24'
                                                    height='24'
                                                    fill='white'
                                                    transform='translate(0 0.70047)'
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    <div className='w-full max-w-[365px]'>
                                        <div className='text-base font-medium text-accent mb-2'>
                                            {organizationName}
                                        </div>
                                        {address && (
                                            <p className='text-accent text-xs font-normal'>
                                                {address}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {
                    // TEMUKAN RUMAH123
                }
                <div className='absolute bottom-8 left-[208px] flex items-center gap-2 z-[2]'>
                    <div className='text-sm font-bold text-accent'>
                        Temukan Saya di
                    </div>
                    <img
                        className='w-[140px] aspect-[140/29]'
                        src={`${process.env.NEXT_PUBLIC_BASE_URL || 'https://rumah123portal.staging.99iddev.net'}/asset-core/images/assets/logo_rumah123_blue.png`}
                        alt='Logo Rumah123.com'
                        draggable='false'
                        decoding='async'
                    />
                </div>
                {
                    // SCAN QR
                }
                <div className='absolute z-[2] bottom-8 right-[38px] flex items-end gap-[23px] justify-end'>
                    <div>
                        <div className='text-sm'>Scan di sini</div>
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='54'
                            height='20'
                            viewBox='0 0 54 20'
                            fill='none'
                        >
                            <path
                                d='M2 9.71505C11.8139 17.4179 35.2172 26.6592 50.3196 2M50.3196 2L36.6642 2.91289M50.3196 2L52 13.6096'
                                stroke='#0D1A35'
                                strokeWidth='3'
                                strokeLinecap='round'
                            />
                        </svg>
                    </div>
                    <div className='relative w-[175px] h-[175px] rounded-lg border-2 border-solid border-black overflow-hidden'>
                        <QRCode
                            ref={qrCodeRef}
                            value={fullUrl}
                            size={149}
                            ecLevel='L'
                            enableCORS
                        />
                    </div>
                </div>
                {
                    // AGENCY LOGO
                }
                {agentType && agentType === 'Agen Korporat' && (
                    <div className='absolute z-[2] top-[25px] right-[38px] flex justify-center items-center w-[170px] h-[60px] rounded-md shadow-md bg-white'>
                        <img
                            src={companyPicture}
                            alt={organizationName}
                            decoding='async'
                            className='h-full object-contain'
                        />
                    </div>
                )}
                <img
                    src={NAME_CARD_FRONT}
                    alt='kartu nama depan'
                    className='absolute top-0 left-0 right-0 bottom-0 w-full h-full object-cover object-center'
                />
            </div>
        );
    },
);

export default CardFront;
